import Api from '@/util/Api';
import { roundCoordinate, type WeatherDay, type WeatherForecast } from './weather-rules';

/**
 * ROBO-830. Signed-in callers need no checkout pass; an anonymous one (a guest) sends one in
 * `hd-pass` (ROBO-476), which the HOST obtains — see {@link WeatherRequest}.
 */
export const WEATHER_FORECAST_PATH = 'checkout/weather/forecast';

/** Daily rows only. The route stops at its own cap (`WeatherApi:MaxForecastDays`, 14 when unset). */
export const FORECAST_DAYS = 14;

export interface WeatherLocation {
    latitude: number;
    longitude: number;
}

/**
 * ROBO-865. How a caller other than the signed-in session reaches the route: `skipAuth` sends no
 * session, and `headers` (awaited per request) supplies what stands in for one — for a guest, a
 * checkout pass and the brand host. The module never obtains either itself: the host does.
 */
export interface WeatherRequest {
    skipAuth?: boolean;
    headers?: () => Promise<Record<string, string>>;
}

/**
 * Nested under one prefix (STYLE.md). The coordinates are rounded to 2 decimals, so two calendars —
 * or the same calendar redrawn — at the same place share ONE request per page load.
 */
export const weatherQueryKeys = {
    all: ['cart-weather'] as const,
    forecast: (location: WeatherLocation) =>
        [
            'cart-weather',
            'forecast',
            {
                latitude: roundCoordinate(location.latitude),
                longitude: roundCoordinate(location.longitude)
            }
        ] as const
};

/**
 * One `POST checkout/weather/forecast` for the rounded location. Throws on any failure (a 401, a
 * 429, a 5xx, a body without `days`) — the hook turns every one of them into "no weather".
 */
export async function fetchWeatherForecast(
    location: WeatherLocation,
    request?: WeatherRequest
): Promise<WeatherDay[]> {
    // A headers() that rejects (no pass to be had) is a failure like any other: no weather.
    const headers = request?.headers ? await request.headers() : undefined;
    const response = await Api.post<Record<string, number>, WeatherForecast>(
        WEATHER_FORECAST_PATH,
        {
            latitude: roundCoordinate(location.latitude),
            longitude: roundCoordinate(location.longitude),
            days: FORECAST_DAYS
        },
        request?.skipAuth ?? false,
        headers ? { headers } : undefined
    );
    if (!response.ok) {
        throw response.error ?? new Error(response.statusText || 'Unable to load the forecast');
    }
    const days = response.data?.days;
    if (!Array.isArray(days)) throw new Error('The forecast had no daily rows');
    return days;
}
